// FLAT BUFFERS
import { Control } from "../../../flatbuffers/generated/control/control";
// INTERFACES
import { BankInterface, BankFloorInterface } from "../../../interfaces/Bank.interface";
import { LiftInterface, LiftFloorInterface } from "../../../interfaces/Lift.interface";

/**
 * Command vocabulary shared by every Terminal command surface.
 *
 * These lived in `TerminalBankCommands` and are re-exported from there for
 * compatibility. They moved here because `useFrontRearDoorAvailability`
 * already imported `bankHasDoorSide` back out of that component, making a
 * component↔hook import cycle that a second consumer would only deepen.
 * Nothing in this module renders, so both can depend on it freely.
 */

export type CommandKind =
  | "ENERGIZE_CAR_CALL"
  | "ENERGIZE_HALL_UP"
  | "ENERGIZE_HALL_DOWN"
  | "LOCK_CAR_CALL"
  | "LOCK_HALL_UP"
  | "LOCK_HALL_DOWN";

export type DoorSide = "FRONT" | "REAR";

export interface CommandSpec {
  key: CommandKind;
  label: string;
  /**
   * Per-lift, per-floor command (true) vs bank-level (false). Hall calls map
   * to the same Redux dispatch as car calls but don't depend on a specific
   * lift.
   */
  perLift: boolean;
  controlFor: (door: DoorSide) => Control;
}

export const COMMAND_SPECS: CommandSpec[] = [
  {
    key: "ENERGIZE_CAR_CALL",
    label: "Energize Car Call",
    perLift: true,
    controlFor: (d) => (d === "FRONT" ? Control.EnergizeCarCall : Control.EnergizeRearCarCall),
  },
  {
    key: "ENERGIZE_HALL_UP",
    label: "Energize Hall Up",
    perLift: false,
    controlFor: (d) =>
      d === "FRONT" ? Control.EnergizeUpHallCall : Control.EnergizeUpRearHallCall,
  },
  {
    key: "ENERGIZE_HALL_DOWN",
    label: "Energize Hall Down",
    perLift: false,
    controlFor: (d) =>
      d === "FRONT" ? Control.EnergizeDownHallCall : Control.EnergizeDownRearHallCall,
  },
  {
    key: "LOCK_CAR_CALL",
    label: "Lock Out Car Call",
    perLift: true,
    controlFor: (d) => (d === "FRONT" ? Control.CarCallLock : Control.RearCarCallLock),
  },
  {
    key: "LOCK_HALL_UP",
    label: "Lock Out Hall Up",
    perLift: false,
    controlFor: (d) => (d === "FRONT" ? Control.UpHallCallLock : Control.UpRearHallCallLock),
  },
  {
    key: "LOCK_HALL_DOWN",
    label: "Lock Out Hall Down",
    perLift: false,
    controlFor: (d) => (d === "FRONT" ? Control.DownHallCallLock : Control.DownRearHallCallLock),
  },
];

/**
 * Resolve whether a specific lift+floor has a working door on the requested
 * side. Per-lift overrides (lift.liftFloors[].frontDoor/rearDoor) take
 * precedence; fallback to bank-level (floor.frontDoor/rearDoor).
 */
export function liftHasDoor(
  lift: LiftInterface | undefined,
  bankFloor: BankFloorInterface,
  side: DoorSide,
): boolean {
  const liftFloors: LiftFloorInterface[] | undefined = lift?.liftFloors;
  const liftFloor = liftFloors?.find((lf) => lf.bankFloorId === bankFloor.id);
  if (liftFloor) {
    return side === "FRONT" ? !!liftFloor.frontDoor : !!liftFloor.rearDoor;
  }
  return side === "FRONT" ? !!bankFloor.frontDoor : !!bankFloor.rearDoor;
}

/** Bank-level: at least one floor has the requested door type. */
export function bankHasDoorSide(bank: BankInterface, side: DoorSide): boolean {
  const floors: BankFloorInterface[] = (bank.bankFloors ?? []) as BankFloorInterface[];
  if (side === "REAR" && !bank.rearDoors) return false;
  return floors.some((f) => (side === "FRONT" ? !!f.frontDoor : !!f.rearDoor));
}
